"use client";

import Link from "next/link";
import { usePathname } from "next/navigation";
import { useEffect, useRef, useState, useSyncExternalStore } from "react";
import { Logo } from "./Logo";
import { LoginModal, openLoginModal, openSignupModal } from "./LoginModal";
import { NavigationPanel } from "./NavigationPanel";
import { LanguageSwitcher } from "./LanguageSwitcher";
import { useI18n } from "@/lib/i18n/I18nProvider";

export type Audience = "personal" | "business";
export const AUDIENCE_LABEL = { personal: "For you", business: "For business" };

const subscribeHydration = () => () => {};

function subscribeScroll(onChange: () => void) {
  window.addEventListener("scroll", onChange, { passive: true });
  return () => window.removeEventListener("scroll", onChange);
}
const compactSnapshot = () => window.scrollY > 48;
const serverCompactSnapshot = () => false;

export function Nav({ audience }: { audience?: Audience }) {
  const pathname = usePathname();
  const { t: dict } = useI18n();
  const t = dict.nav;
  const hydrated = useSyncExternalStore(
    subscribeHydration,
    () => true,
    () => false,
  );
  const activeAudience =
    audience ?? (pathname === "/business" ? "business" : "personal");
  const [menu, setMenu] = useState<
    "products" | "solutions" | "developers" | "mobile" | null
  >(null);
  const compact = useSyncExternalStore(
    subscribeScroll,
    compactSnapshot,
    serverCompactSnapshot,
  );
  const [hidden, setHidden] = useState(false);
  const [navFocused, setNavFocused] = useState(false);
  const [previousPathname, setPreviousPathname] = useState(pathname);
  if (previousPathname !== pathname) {
    setPreviousPathname(pathname);
    setMenu(null);
    setHidden(false);
    setNavFocused(false);
  }
  const concealed = hidden && !menu && !navFocused;
  const navRef = useRef<HTMLElement>(null);
  const trigger = useRef<HTMLButtonElement>(null);
  const mobileTrigger = useRef<HTMLButtonElement>(null);
  const solutionTrigger = useRef<HTMLButtonElement>(null);
  const developerTrigger = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    let anchor = window.scrollY;
    let frame = 0;
    const reduced = matchMedia("(prefers-reduced-motion: reduce)");
    const update = () => {
      frame = 0;
      const y = Math.max(0, window.scrollY);
      if (y <= 160 || reduced.matches) {
        setHidden(false);
        anchor = y;
      } else if (Math.abs(y - anchor) >= 12) {
        setHidden(y > anchor);
        anchor = y;
      }
    };
    const scroll = () => {
      if (!frame) frame = requestAnimationFrame(update);
    };
    const keyboard = (event: KeyboardEvent) => {
      if (event.key === "Tab") setHidden(false);
    };
    window.addEventListener("scroll", scroll, { passive: true });
    document.addEventListener("keydown", keyboard);
    reduced.addEventListener("change", update);
    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener("scroll", scroll);
      document.removeEventListener("keydown", keyboard);
      reduced.removeEventListener("change", update);
    };
  }, []);
  useEffect(() => {
    if (!menu) return;
    function close(event: KeyboardEvent) {
      if (event.key === "Escape") {
        setMenu(null);
        (menu === "mobile"
          ? mobileTrigger
          : menu === "solutions"
            ? solutionTrigger
            : menu === "developers"
              ? developerTrigger
              : trigger
        ).current?.focus();
      }
    }
    function outside(event: PointerEvent) {
      if (!navRef.current?.contains(event.target as Node)) setMenu(null);
    }
    document.addEventListener("keydown", close);
    document.addEventListener("pointerdown", outside);
    return () => {
      document.removeEventListener("keydown", close);
      document.removeEventListener("pointerdown", outside);
    };
  }, [menu]);
  useEffect(() => {
    const breakpoint = matchMedia("(max-width: 1179px)");
    const reset = () => {
      setMenu(null);
      setHidden(false);
    };
    breakpoint.addEventListener("change", reset);
    return () => breakpoint.removeEventListener("change", reset);
  }, []);
  const close = () => setMenu(null);
  return (
    <>
      <header
        className={`ec-nav ec-nav-islands ${compact ? "is-compact" : ""} ${concealed ? "is-concealed" : ""}`}
        inert={concealed || undefined}
        onFocusCapture={() => setNavFocused(true)}
        ref={navRef}
        onBlur={(event) => {
          if (
            !event.currentTarget.contains(event.relatedTarget as Node | null)
          ) {
            setMenu(null);
            setNavFocused(false);
          }
        }}
      >
        <div className="ec-container ec-nav-row">
          <div className="ec-nav-island ec-nav-brand">
            <button
              disabled={!hydrated}
              className="ec-menu-toggle"
              ref={mobileTrigger}
              aria-label={menu === "mobile" ? t.closeNav : t.openNav}
              aria-expanded={menu === "mobile"}
              aria-controls="ec-mobile-menu"
              onClick={() => setMenu(menu === "mobile" ? null : "mobile")}
            >
              <span className="ec-hamburger" aria-hidden="true">
                <i />
                <i />
              </span>
            </button>
            <Link href="/" aria-label={t.home} onClick={close}>
              <Logo />
            </Link>
          </div>
          <nav
            className="ec-audience ec-nav-island"
            aria-label={t.audience}
            data-audience={activeAudience}
          >
            <span className="ec-audience-thumb" aria-hidden="true" />
            <Link
              href="/"
              aria-current={activeAudience === "personal" ? "page" : undefined}
              onClick={close}
            >
              {t.audiences.personal}
            </Link>
            <Link
              href="/business"
              aria-current={activeAudience === "business" ? "page" : undefined}
              onClick={close}
            >
              {t.audiences.business}
            </Link>
          </nav>
          <div className="ec-nav-island ec-nav-links-island">
            <div className="ec-nav-links-fold">
              <nav
                className="ec-desktop-nav"
                id="ec-navigation-links"
                aria-label={t.main}
              >
                <button
                  disabled={!hydrated}
                  ref={trigger}
                  aria-expanded={menu === "products"}
                  aria-controls="ec-products-menu"
                  onClick={() =>
                    setMenu(menu === "products" ? null : "products")
                  }
                >
                  {t.products}{" "}
                  <svg
                    className="ec-nav-chevron"
                    viewBox="0 0 16 16"
                    aria-hidden="true"
                  >
                    <path d="m4 6 4 4 4-4" />
                  </svg>
                </button>
                <button
                  disabled={!hydrated}
                  ref={solutionTrigger}
                  aria-expanded={menu === "solutions"}
                  aria-controls="ec-solutions-menu"
                  onClick={() =>
                    setMenu(menu === "solutions" ? null : "solutions")
                  }
                >
                  {t.solutions}{" "}
                  <svg
                    className="ec-nav-chevron"
                    viewBox="0 0 16 16"
                    aria-hidden="true"
                  >
                    <path d="m4 6 4 4 4-4" />
                  </svg>
                </button>
                <button
                  disabled={!hydrated}
                  ref={developerTrigger}
                  aria-expanded={menu === "developers"}
                  aria-controls="ec-developers-menu"
                  onClick={() =>
                    setMenu(menu === "developers" ? null : "developers")
                  }
                >
                  {t.developers}{" "}
                  <svg
                    className="ec-nav-chevron"
                    viewBox="0 0 16 16"
                    aria-hidden="true"
                  >
                    <path d="m4 6 4 4 4-4" />
                  </svg>
                </button>
                <Link href="/pricing" onClick={close}>
                  {t.pricing}
                </Link>
              </nav>
            </div>
          </div>
          <div className="ec-nav-actions ec-nav-island">
            <LanguageSwitcher />
            <button
              disabled={!hydrated}
              className="ec-login"
              onClick={() => {
                close();
                openLoginModal();
              }}
            >
              {t.login}
            </button>
            <button
              disabled={!hydrated}
              className="ec-button ec-button-small"
              onClick={() => {
                close();
                openSignupModal();
              }}
            >
              {t.getStarted}
            </button>
          </div>
        </div>
        {menu && (
          <NavigationPanel
            kind={menu}
            onNavigate={close}
            onLogin={() => {
              close();
              openLoginModal();
            }}
            onSignup={() => {
              close();
              openSignupModal();
            }}
          />
        )}
      </header>
      <LoginModal />
    </>
  );
}
